import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Box, CircularProgress, Divider, IconButton, Typography } from "@mui/material";

import {
  getBillNoteByIdAction,
  addBillNoteAction,
} from "../../../redux/actions/cartAction";
import CustomModal from "../../common/custom-modal/custom-modal";
import { CustomButton, CustomTextBox } from "../../atoms";
import { Colors } from "../../../configs";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { NoteOccasion } from "../../../typings/enum/noteOccasions";
import ReplyIcon from "@mui/icons-material/Reply";
import { CartSliceAction } from "../../../redux/slice/cartSlice";
import BillNotesCard from "./billNotesCard";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { IBillResponse } from "../../../typings/interfaces/responses/new-bill-response";
import TopRightButton from "../../common/top-right-button/top-right-button";

export interface INotesModalProps {
  open: boolean;
  onClose(): void;
}

const BillNotesModal: FC<INotesModalProps> = ({ open, onClose }) => {
  const [newNote, setNewNote] = useState("");
  const { customer } = useAppSelector((state) => state.customer);
  // const [selectedOccations, setSelectedOccasions] = useState<string[]>([]);
  const [mode, setMode] = useState<"temp" | "perm">("temp");

  const dispatch = useAppDispatch();
  const params = useParams();
  const customerId = params.id;
  const {
    bills,
    billNotes,
    addBillNoteLoading,
    temporaryBillNotes,
    selectedSession,
  } = useAppSelector((state) => state.cart);
  const { user } = useAppSelector((state) => state.auth);
  const snackbar = useSnackbarContext();
  const [targetBill, setTargetBill] = useState<IBillResponse | undefined>(
    bills?.find((bill) => bill.sessionId === selectedSession?.sessionId)
  );

  // const billId: number = Number(bill?.id ?? "1"); // change later

  useEffect(() => {
    setTargetBill(
      bills?.find((bill) => bill.sessionId === selectedSession?.sessionId)
    );
    if (targetBill && targetBill.id) {
      setMode("perm");
      dispatch(getBillNoteByIdAction({ billId: targetBill.id }));
    } else {
      setMode("temp");
    }
  }, [bills, customerId]);

  const handleNewNoteTextChange = (note: string) => {
    setNewNote(note);
  };

  const handleOnAddNote = async () => {
    if (newNote.length > 0) {
      if (customerId && user) {
        if (mode === "perm" && targetBill) {
          // alert("adding perm note");
          await dispatch(
            addBillNoteAction({
              note: newNote,
              occasions: [] as NoteOccasion[],
              // merchantUserId: Number(user.id), // This must be fixed. this is not the admin id which should used
              merchantUserId: 1,
              customerId: Number(customerId),
              billId: targetBill.id,
            })
          ).then(() => {
            dispatch(getBillNoteByIdAction({ billId: targetBill!.id }));
          });
        } else {
          const tempId = uuidv4();
          // alert("adding temp note");
          dispatch(
            CartSliceAction.setTemporaryBillNotes([
              {
                tempId: tempId,
                note: newNote,
                occasions: [] as NoteOccasion[],
                // merchantUserId: Number(user.id), // This must be fixed. this is not the admin id which should used
                merchantUserId: 1,
                customerId: Number(customerId),
                cashierFirstName: user.firstName,
                cashierLastName: user.lastName,
                date: new Date().toISOString(),
                // Required so payment-modal.tsx's checkout-time print
                // builder can find this note again (it matches temp notes
                // to the bill being printed by sessionId) - left out here
                // before, this field was always undefined at runtime
                // (temporaryBillNotesInterface declares it required, but
                // the setTemporaryBillNotes reducer's payload isn't typed,
                // so nothing caught the omission), which meant the filter
                // never matched and a bill note typed at check-in never
                // made it onto the printed receipt.
                sessionId: selectedSession?.sessionId ?? "",
              },
              ...temporaryBillNotes,
            ])
          );
        }
        setNewNote("");
      } else {
        snackbar.showSnackbar("Failed to detect customer/cashier", "error");
      }
    } else {
      snackbar.showSnackbar("Cannot add empty notes", "warning");
    }
  };

  // const handleOnAddNote = async () => {
  //   if (newNote.length > 0) {
  //     if (customerId && user) {
  //       const tempId = uuidv4();
  //       bill !== null && Number(bill.id) > 0
  //         ? await dispatch(
  //             addBillNoteAction({
  //               note: newNote,
  //               occasions: [] as NoteOccasion[],
  //               // merchantUserId: Number(user.id), // This must be fixed. this is not the admin id which should used
  //               merchantUserId: 1,
  //               customerId: Number(customerId),
  //               billId: Number(bill.id),
  //             })
  //           ).then((response) => {
  //             // if (response) {
  //             //   console.log(JSON.stringify(response));
  //             // } else {
  //             // }
  //             dispatch(getBillNoteByIdAction({ billId: String(bill.id) }));
  //           })
  //         : dispatch(
  //             CartSliceAction.setTemporaryBillNotes([
  //               {
  //                 tempId: tempId,
  //                 note: newNote,
  //                 occasions: [] as NoteOccasion[],
  //                 // merchantUserId: Number(user.id), // This must be fixed. this is not the admin id which should used
  //                 merchantUserId: 1,
  //                 customerId: Number(customerId),
  //                 cashierFirstName: user.firstName,
  //                 cashierLastName: user.lastName,
  //                 date: new Date().toISOString(),
  //               },
  //               ...temporaryBillNotes,
  //             ])
  //           );
  //       setNewNote("");
  //     }
  //   } else {
  //     snackbar.showSnackbar("Cannot add empty notes", "warning");
  //   }
  // };

  return (
    <CustomModal
      title={"Bill Notes"}
      openModal={open}
      width="55.4464286rem"
      height="90vh"
      sx={{ marginTop: "2rem" }}
      onClose={onClose}
      hideCloseButton={true}
    >
      <TopRightButton variation="Back" onClick={onClose} />
      <Box
        sx={{
          height: "calc(90vh - 36rem)",
          overflowY: "auto",
          msOverflowStyle: "none",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          touchAction: "pan-y",
        }}
      >
        {addBillNoteLoading ? (
          <Box
            sx={{
              textAlign: "center",
              py: "14rem",
              fontSize: "1.8rem",
              opacity: 0.5,
            }}
          >
            <CircularProgress />
          </Box>
        ) : mode === "perm" ? (
          billNotes == null || billNotes.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: "14rem",
                fontSize: "1.8rem",
                opacity: 0.5,
              }}
            >
              No notes for this bill
            </Box>
          ) : (
            billNotes.map((note, index) => (
              <BillNotesCard key={index} note={note} noteMode={"perm"} />
              /*
                <BillNotesCard
                key={note.id}
                description={note.note}
                noteBy={`${note.reportedUserFirstName} ${note.reportedUserLastName}`}
                date={note.createdTime ? new Date(note.createdTime) : null}
                noteId={note.id}
                noteMode={"perm"}
              />
              */
            ))
          )
        ) : temporaryBillNotes == null || temporaryBillNotes.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: "14rem",
              fontSize: "1.8rem",
              opacity: 0.5,
            }}
          >
            No notes for this bill
          </Box>
        ) : (
          temporaryBillNotes.map((note, index) => (
            <BillNotesCard
              key={note.tempId}
              note={{
                note: note.note,
                reportedUserFirstName: note.cashierFirstName,
                reportedUserLastName: note.cashierLastName,
                customerId: note.customerId,
                noteShowOccasion: note.occasions,
                reportedUserId: note.merchantUserId,
                createdTime: new Date().toISOString(),
                customerFirstName: customer?.firstName ?? "",
                customerLastName: customer?.lastName ?? "",
                lastModifiedTime: new Date().toISOString(),
                billId: targetBill?.id ?? 0,
                id: note.tempId,
              }}
              noteMode={"temp"}
            />
            /*
            <BillNotesCard
              key={note.tempId}
              description={note.note}
              noteBy={`${note.cashierFirstName} ${note.cashierLastName}`}
              date={note.date ? new Date(note.date) : null}
              noteId={note.tempId}
              noteMode={"temp"}
            />
            */
          ))
        )}
      </Box>
      <Divider sx={{ marginTop: "1.42857143rem" }} />
      <Typography
        sx={{
          fontSize: "1.71428571rem",
          fontWeight: 600,
          color: Colors.TEXT_GRAY_DARK,
          marginTop: "1rem",
          marginBottom: "0.5rem",
        }}
      >
        Add new note
      </Typography>
      <CustomTextBox
        test-name="new-note-textbox"
        multiline
        autoFocus
        styles={{ backgroundColor: "transparent" }}
        sx={{
          width: "100%",
          background: Colors.BACKGROUND_GRAY,
          border: "none",
          borderRadius: "0.214285714rem",
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "& .MuiInputBase-input": {
            height: "9rem",
            minHeight: "9rem",
            maxHeight: "9rem",
          },
        }}
        value={newNote}
        onChange={(event) => handleNewNoteTextChange(event.target.value)}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          marginTop: "3rem",
        }}
      >
        <CustomButton
          test-name="add-note-button"
          sx={{
            background: Colors.PRIMARY,
            color: Colors.WHITE,
            textTransform: "none",
            fontSize: "1.42857143rem",
            height: "3.85714286rem",
            width: "60%",
            borderRadius: "0.3rem",
          }}
          disabled={newNote.length === 0 || addBillNoteLoading}
          onClick={handleOnAddNote}
        >
          Add New Note
        </CustomButton>
      </Box>
    </CustomModal>
  );
};

export default BillNotesModal;
